import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';

const Wrap = styled.div`
  padding: 4px 0 24px;
`;

// Tracking view of one offboarding's sub-process (IT access, handover,
// equipment, legal docs, exit interview). Reuses the standalone page's config
// hook — columns/labels/formatting come for free — but pins the query to a
// single offboarding and drops the now-redundant person column.
//
// Default is read-only. A parent can opt specific tabs into row actions via
// `buildRowActions(row, api, openConfirm)` and a status banner via
// `renderBanner(rows, api)`: the BT Erişim tab uses both, because keeping
// verification actions only on the standalone page left users staring at an
// all-revoked list with nothing to click while the process refused to advance
// (customer feedback).
export default function OffboardingSubListTab({
  useApiHook, offboardingId, title, buildRowActions, renderBanner,
}) {
  const queryParams = useMemo(
    () => ({ 'offboardingId[eq]': offboardingId, pageSize: 200, pageNumber: 1 }),
    [offboardingId],
  );

  const apiResult = useApiHook(queryParams);
  const { fetching, data, columns: baseColumns } = apiResult;
  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const columns = useMemo(() => {
    const base = (baseColumns || []).filter((c) => c.name !== 'personFullName');
    if (!buildRowActions) return base;
    return [
      ...base,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildRowActions(row, apiResult, setConfirm) || [];
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, buildRowActions, data, apiResult]);

  return (
    <Wrap>
      {renderBanner ? renderBanner(data, apiResult) : null}
      {/* serverSide (default) renders the data prop directly — the proven mode
          the standalone list pages use. Client mode (serverSide=false) failed
          to reflect async-loaded rows here. The hook already fetches the full
          set (pageSize 200) so a single page shows everything. */}
      <DataTable
        title={title}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={data.length}
        pageSize={50}
        pageIndex={0}
        disableFilters
      />
      <ConfirmDialog
        isOpen={!!confirm}
        message={confirm?.message}
        loading={confirmBusy}
        onConfirm={async () => {
          setConfirmBusy(true);
          try { await confirm.onYes(); } finally { setConfirmBusy(false); setConfirm(null); }
        }}
        onCancel={() => setConfirm(null)}
      />
    </Wrap>
  );
}
